import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import type { AdjacentRooms } from "building-types/framing/wallFramingTypes";
import type { RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass, RoomKind } from "building-types/rooms/roomTypesEnum";
import type { WallSideRoomDescriptor } from "building-types/walls/wallDesciptorTypes";
import type { WallPlaneSideRoom, WallPlaneSides } from "building-types/walls/wallPlaneSideTypes";
import { WallCondition } from "building-types/walls/wallType";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import {
  faceNodeGetParentHandle,
  volumeNodeParentHandleGetRoomId,
} from "../../buildingGraphFilter/entityFilterSelectors";

export function determineWallCondition(
  leftWallRoom: Pick<WallSideRoomDescriptor, "roomKind" | "roomClass">,
  rightWallRoom: Pick<WallSideRoomDescriptor, "roomKind" | "roomClass">,
): WallCondition {
  const { roomKind: leftRoomKind, roomClass: leftRoomClass } = leftWallRoom;
  const { roomKind: rightRoomKind, roomClass: rightRoomClass } = rightWallRoom;
  // handles null values as exterior
  const isRightRoomEffectivelyExterior = rightRoomKind === RoomKind.EXTERIOR || rightRoomKind === RoomKind.BOUNDARY;
  const isLeftRoomEffectivelyExterior = leftRoomKind === RoomKind.EXTERIOR || leftRoomKind === RoomKind.BOUNDARY;

  const isRightRoomGarage = rightRoomClass === RoomClass.GARAGE;
  const isLeftRoomGarage = leftRoomClass === RoomClass.GARAGE;
  const isGarageWall = isRightRoomGarage || isLeftRoomGarage;

  if (isRightRoomEffectivelyExterior && isLeftRoomEffectivelyExterior) {
    return WallCondition.EXTERIOR_INTERIOR;
  }
  if (isRightRoomEffectivelyExterior !== isLeftRoomEffectivelyExterior) {
    return isGarageWall ? WallCondition.EXTERIOR_GARAGE : WallCondition.EXTERIOR_INTERIOR;
  }
  return isGarageWall ? WallCondition.INTERIOR_GARAGE : WallCondition.INTERIOR_INTERIOR;
}

export function wallConditionFromAdjacentRooms(adjacentRooms: AdjacentRooms, rooms: Table<Room>): WallCondition {
  const [negativeSideRoom, positiveSideRoom] = adjacentRooms;
  return determineWallCondition(
    wallSideRoomDescriptorFromHandle(negativeSideRoom, rooms),
    wallSideRoomDescriptorFromHandle(positiveSideRoom, rooms),
  );
}

function wallSideRoomDescriptorFromHandle(
  handle: RoomHandle | null,
  rooms: Table<Room>,
): Pick<WallSideRoomDescriptor, "roomKind" | "roomClass"> {
  if (!handle) {
    return { roomKind: RoomKind.EXTERIOR, roomClass: RoomClass.EXTERIOR };
  }
  const room = rooms[handle.id];
  if (!room) {
    return { roomKind: RoomKind.EXTERIOR, roomClass: RoomClass.EXTERIOR };
  }
  // Enclosure-aware: a non-null room can still be effectively exterior (e.g. a porch/deck), so the
  // room kind is derived from its enclosure rather than assuming any room is interior.
  const roomKind = isInteriorRoomEnclosure[room.attributes.roomEnclosure] ? RoomKind.INTERIOR : RoomKind.EXTERIOR;
  return { roomKind, roomClass: room.attributes.roomClass };
}

function selectPrioritySideRoom(rooms: readonly Room[]): WallPlaneSideRoom | null {
  if (rooms.length === 0) {
    return null;
  }
  // priority sort => lowest room class is highest priority
  const sorted = [...rooms].sort((a, b) => a.attributes.roomClass - b.attributes.roomClass);
  const room = sorted[0];
  const roomKind = isInteriorRoomEnclosure[room.attributes.roomEnclosure] ? RoomKind.INTERIOR : RoomKind.EXTERIOR;
  return { room, roomKind };
}

export function resolveWallPlaneSideRoomsFromFaceNodes(
  faceNodeIds: readonly string[],
  buildingGraph: BuildingGraph,
  rooms: Table<Room>,
): WallPlaneSides {
  const negativeRooms: Room[] = [];
  const positiveRooms: Room[] = [];

  for (const faceNodeId of faceNodeIds) {
    const faceNode = buildingGraph.faceNodes[faceNodeId];
    assertIsNotNullOrUndefined(faceNode);

    // Only room-backed volumes contribute to wall-side rooms; face nodes whose parent volume is
    // not a room (e.g. roof or floor volumes) carry no side room and are skipped.
    const roomId = volumeNodeParentHandleGetRoomId(faceNodeGetParentHandle(faceNode, buildingGraph));
    if (roomId === undefined) {
      continue;
    }

    const room = rooms[roomId];
    assertIsNotNullOrUndefined(room);

    if (faceNode.outwardSideDirection === "WALL_PLANE_POSITIVE_NORMAL") {
      positiveRooms.push(room);
    } else {
      negativeRooms.push(room);
    }
  }

  return {
    negativeSide: selectPrioritySideRoom(negativeRooms),
    positiveSide: selectPrioritySideRoom(positiveRooms),
  };
}

function wallSideRoomDescriptorFromSide(
  side: WallPlaneSideRoom | null,
): Pick<WallSideRoomDescriptor, "roomKind" | "roomClass"> {
  if (!side) {
    return { roomKind: RoomKind.EXTERIOR, roomClass: RoomClass.EXTERIOR };
  }
  return { roomKind: side.roomKind, roomClass: side.room.attributes.roomClass };
}

export function wallConditionFromWallPlaneSides(sides: WallPlaneSides): WallCondition {
  return determineWallCondition(
    wallSideRoomDescriptorFromSide(sides.negativeSide),
    wallSideRoomDescriptorFromSide(sides.positiveSide),
  );
}

export function adjacentRoomHandlesFromWallPlaneSides(sides: WallPlaneSides): AdjacentRooms {
  return [sides.negativeSide?.room.roomHandle ?? null, sides.positiveSide?.room.roomHandle ?? null];
}
